"use client";
import { cn } from "@/lib/utils";
import { ActiveMemory } from "@/memory/types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { cloneNode, getOrgAndStoreId } from "../ui-script/utils";
import { Product } from "../types";
import { BACKEND_URL } from "../ui-script/product-list";
import { SDKConfiguration } from "@/inference/setup/config";
import { shuffle } from "./utils/shuffle";
import PreviousRecommendations from "./previous-recommendations";
import { ScoringHandler } from "@/dataprocessor/scoring";
import { handleItemsByScore, sortHandlesByQuantity } from "@/utils";

interface MemoryRecommendationPilletsProps {
  subcategory?: string;
  memoryData: ActiveMemory;
}

interface ProductDetails {
  title: string;
  description: string;
  price: string;
  imageUrl: string;
}

const renderCloneNode = async (item: Product) => {
  const templateNode = window.helium.datastore.memory.html.product;
  const handle =
    window.helium.datastore.memory.html.productHandle ||
    window.location.pathname.split("/").at(-1);

  if (!templateNode) return null;

  const existingProduct = (
    await window.helium.datastore.getProduct({
      id: handle ?? "",
    })
  ).details;
  // if (window.location.pathname.includes("product")) {
  //   existingProduct = window.helium.datastore.memory.product["test"].details;
  // }

  const node = cloneNode(templateNode, item, existingProduct);

  node.className += " !hp-w-[150px]";
  return { __html: node.outerHTML };
};

const CardSkeleton = ({ index }: { index: number }) => (
  <div
    className="hp-flex hp-h-[225px] hp-w-[192px] hp-select-none hp-flex-col hp-gap-2 hp-rounded-2xl hp-bg-white hp-p-2 hp-shadow-lg"
    key={index}
  >
    <div className="hp-h-[168px] hp-w-[176px] hp-animate-pulse hp-rounded-xl hp-bg-gray-200 sm:hp-h-full"></div>{" "}
    <div className="hp-flex hp-flex-1 hp-flex-col hp-gap-3">
      <div className="hp-flex hp-flex-1 hp-flex-col hp-gap-3">
        <div className="hp-h-[12px] hp-w-full hp-animate-pulse hp-rounded-md hp-bg-gray-200"></div>
      </div>
    </div>
  </div>
);

const orgId = window.helium.brandData.orgId;
const storeId = window.helium.brandData.storeId;
const filterUrl = `${SDKConfiguration.STAGING_SERVICE_ENDPOINT}/meilisearch/ui/searchByfilter`;
const previousTab = "previous-recommendations";

function MemoryRecommendationPillets({
  subcategory,
  memoryData,
}: MemoryRecommendationPilletsProps) {
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    if (memoryData) setDataReady(true);
  }, [memoryData]);

  const result = useMemo(() => {
    if (!dataReady) return []; // Prevent premature execution
    return getFormattedFunctionalPreferences(memoryData, subcategory);
  }, [dataReady, memoryData, subcategory]);

  const [activeTab, setActiveTab] = useState<string>(
    window.location.pathname.includes("/checkout")
      ? Object.keys(memoryData)[0]
      : previousTab,
  );

  const [productList, setProductList] = useState<ProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uiProducts, setUiProducts] = useState([]);

  const fetchProductDetails = async (orgId: string, storeId: string) => {
    const index = getOrgAndStoreId(orgId, storeId);
    setIsLoading(true);
    try {
      const response = await fetch(filterUrl, {
        method: "POST",
        body: JSON.stringify({
          query: "",
          index: index,
          filters: activeTab,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch product details");
      }
      const productResult: ProductDetails = await response.json();
      const productsPromise = Promise.all(
        productResult.data?.hits.map(async (item) => {
          const details = await window.helium.datastore.getProduct({
            id: item.id,
          });
          return details.details;
        }),
      );
      const products = await productsPromise;
      setProductList(products);
    } catch (error) {
      console.error(error);
      setActiveTab("");
      setProductList([]);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const fetchPreviousRecommendation = async () => {
    try {
      setIsLoading(true);

      const productScore = sortHandlesByQuantity(
        ScoringHandler.Product.getComputedProductScores(),
      );

      const products = await Promise.all(
        productScore.map(async (item) => {
          try {
            const details = await window.helium.datastore.getProduct({
              id: item.id,
            });
            const tags = await window.helium.datastore.getTags({ id: item.id });

            if (
              tags?.subcategory.toLowerCase() === subcategory?.toLowerCase()
            ) {
              return details.details; // Return product details if it matches
            }
            return null; // Return null if it doesn't match
          } catch (error) {
            console.error(`Error fetching product ${item.id}:`, error);
            return null; // Return null if an error occurs
          }
        }),
      );

      const validProducts = products.filter((el) => el !== null);

      setIsLoading(false);

      setProductList([...validProducts]);

      if (validProducts.length === 0 && result && result.length > 0) {
        setActiveTab(result[0].filterTagName);
        return;
      }
    } catch (error) {
      console.error("Error in fetchPreviousRecommendation:", error);
    }
  };

  useEffect(() => {
    if (activeTab !== "" && activeTab !== previousTab) {
      fetchProductDetails(orgId, storeId);
    } else if (activeTab === previousTab) {
      fetchPreviousRecommendation();
    } else {
      setUiProducts([]);
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchRenderedProducts = async () => {
      const productHtmls = await Promise.all(
        productList.map((item) => renderCloneNode(item)),
      );

      setUiProducts(productHtmls.map((html) => html.__html));
    };

    fetchRenderedProducts();
  }, [productList]);

  if (
    !subcategory &&
    !window.location.pathname.includes("/checkout") &&
    window.location.pathname.includes("product")
  ) {
    return null;
  }

  if (!window.location.pathname.includes("product")) {
    return <CollectionMemoryRecommendtaion memoryData={memoryData} />;
  }

  return (
    <div className="hp-py-1 hp-px-1">
      {result.length > 0 ? (
        <>
          <div className="hp-heading">You have your eyes on</div>
          <div className="hp-flex hp-gap-2 hp-overflow-x-auto no-scrollbar hp-whitespace-nowrap hp-rounded-md hp-p-2">
            <PreviousRecommendations
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              setSelectedSubTab={setActiveTab}
              type="product"
            />
            {result.map((item, index) => {
              if (item.tabName.toLowerCase().includes("false")) return;

              return (
                <span
                  key={index}
                  className={cn(
                    "hp-inline-flex hp-cursor-pointer hp-items-center hp-gap-x-1.5 hp-rounded-full hp-bg-gray-100 hp-px-3 hp-py-1 hp-text-xs hp-font-medium hp-text-gray-600",
                    {
                      "hp-border hp-border-black hp-text-black":
                        activeTab === item.filterTagName,
                    },
                  )}
                  onClick={() => setActiveTab(item.filterTagName)}
                >
                  {item.tabName.split("True")[1] || item.tabName}
                </span>
              );
            })}
          </div>
          <div className="hp-mt-2 swimlane2 hp-items-baseline">
            {isLoading
              ? Array.apply(null, Array(3)).map((_, index) => (
                  <CardSkeleton index={index} />
                ))
              : uiProducts
                ? uiProducts.map((html, i) => (
                    <div
                      key={i}
                      dangerouslySetInnerHTML={{ __html: html }}
                      className="hp-w-[200px]"
                    />
                  ))
                : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default MemoryRecommendationPillets;

function buildFilterTagName(
  key: string,
  value: string,
  subcategory: string = "",
  gender: string = "",
) {
  let filter = `tags.visual.${key}="${value}"`;

  if (subcategory) {
    filter += ` AND subcategory="${subcategory}"`;
  }

  if (gender) {
    filter += ` AND tags.persona.gender="${gender}"`;
  }

  return filter;
}
export function getFormattedFunctionalPreferences(
  data: ActiveMemory,
  subcategory: string,
  stopShuffle: boolean = false,
) {
  const visual =
    data[subcategory?.toLowerCase()]?.behavioral?.preferences?.visual || {};
  if (Object.keys(visual).length === 0) {
    return [];
  }

  const gender =
    data[subcategory?.toLowerCase()]?.behavioral?.preferences?.persona
      ?.gender || "";

  const response = Object.entries(visual)
    .filter(
      ([key, value]) =>
        value.toLowerCase() !== "none" &&
        !key.toLowerCase().includes("color") &&
        !key.toLowerCase().includes("logo"),
    )
    .map(([key, value]) => {
      let formattedValue = value.replace(/\bYes\b/gi, "").trim();

      formattedValue = formattedValue
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      const formattedKey =
        key.split("_")[0].charAt(0).toUpperCase() + key.split("_")[0].slice(1);
      const keyParts = formattedKey.split(" ");
      const lastKeyElement = keyParts[keyParts.length - 1];

      const lastWord = formattedValue?.split(" ")?.pop();

      const finalString =
        lastKeyElement.toLowerCase().includes(lastWord.toLowerCase()) ||
        lastWord.toLowerCase().includes(lastKeyElement.toLowerCase())
          ? formattedValue
          : `${formattedValue} ${formattedKey.toLowerCase().includes("primary") || formattedKey.toLowerCase().includes("secondary") || formattedKey.toLowerCase().includes("tertiary") ? "" : formattedKey}`;
      return {
        tabName: `${finalString}`.trim(),
        filterTagName: buildFilterTagName(key, value, subcategory, gender),
      };
    });

  if (stopShuffle) return response;
  return shuffle(response);
}

export const CollectionMemoryRecommendtaion = ({
  memoryData,
}: {
  memoryData: ActiveMemory;
}) => {
  const [selectedTab, setSelectedTab] = useState(Object.keys(memoryData)[0]);
  const intialFunctional = getFormattedFunctionalPreferences(
    memoryData,
    Object.keys(memoryData)[0],
  );
  const [subTabs, setSubTabs] = useState(intialFunctional || "");
  const [selectedSubTab, setSelectedSubTab] = useState(
    intialFunctional[0]?.filterTagName || "",
  );

  const [productList, setProductList] = useState<ProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uiProducts, setUiProducts] = useState([]);
  const [collectionSubCategories, setCollectionSubCategories] = useState([]);

  async function getCollectionProductHandles() {
    const productListElements = document.querySelectorAll(
      '[data-helium-name="product-list"]',
    );
    const collectionProductHandleList: string[] = [];
    if (productListElements.length > 0) {
      Array.from(productListElements[0].children).forEach((ele) => {
        const handle = ele.getAttribute("data-helium-product-handle");
        if (handle) {
          collectionProductHandleList.push(handle);
        }
      });
    }

    if (collectionProductHandleList.length > 0) {
      const subcategory = await Promise.all(
        collectionProductHandleList.map(async (item) => {
          try {
            const tags = await window.helium.datastore.getTags({
              id: item,
            });
            return tags?.subcategory.toLowerCase() || null;
          } catch (error) {
            console.error(`Error fetching product ${item}:`, error);
            return null;
          }
        }),
      );

      const uniqueSubcategories = [...new Set(subcategory)].filter(
        Boolean,
      ) as string[]; // Filter out nulls and make sure it is string[]

      setCollectionSubCategories(uniqueSubcategories);
    }
  }

  // Handle tab selection
  const handleTabClick = (tab: string) => {
    setSelectedTab(tab);
    const visualSubTabsList = getFormattedFunctionalPreferences(
      memoryData,
      tab,
      true,
    );
    if (visualSubTabsList.length === 0) {
      setUiProducts([]);
      setSubTabs([]);
      setSelectedSubTab(""); // Clear selected sub-tab if there are no sub-tabs
      return;
    }
    setSubTabs(visualSubTabsList);
    setSelectedSubTab(visualSubTabsList[0]?.filterTagName || "");
  };

  const fetchCollectionProducts = async (orgId: string, storeId: string) => {
    setIsLoading(true);
    const index = getOrgAndStoreId(orgId, storeId);
    try {
      const response = await fetch(filterUrl, {
        method: "POST",
        body: JSON.stringify({
          query: "",
          index: index,
          filters: selectedSubTab,
          orgId: orgId,
          storeId: storeId,
          userId:
            window.helium.userToken ?? localStorage.getItem("userToken") ?? "",
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch product details");
      }
      const productResult: ProductDetails = await response.json();
      const productsPromise = Promise.all(
        productResult.data?.hits.map(async (item) => {
          const details = await window.helium.datastore.getProduct({
            id: item.id,
          });
          return details.details;
        }),
      );
      const products = await productsPromise;
      setProductList(products);
    } catch (error) {
      console.error(error);
      setSelectedSubTab("");
      setProductList([]);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const fetchPreviousRecommendation = async () => {
    try {
      setIsLoading(true);
      const productScore = sortHandlesByQuantity(
        ScoringHandler.Product.getComputedProductScores(),
      );

      const products = await Promise.all(
        productScore.map(async (item) => {
          try {
            const details = await window.helium.datastore.getProduct({
              id: item.id,
            });
            return details.details;
          } catch (error) {
            console.error(`Error fetching product ${item.id}:`, error);
            return null;
          }
        }),
      );
      const validProducts = products.filter(Boolean); // Remove null values

      const productHtmls = await Promise.all(
        validProducts.map((item) => renderCloneNode(item)),
      );

      setIsLoading(false);
      setUiProducts(productHtmls.map((html) => html.__html));
    } catch (error) {
      console.error("Error in fetchPreviousRecommendation:", error);
    }
  };

  useEffect(() => {
    if (selectedTab !== previousTab) return;
    fetchPreviousRecommendation();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedSubTab !== "") {
      fetchCollectionProducts(orgId, storeId);
    } else if (selectedSubTab === "" && selectedTab === previousTab) {
      fetchPreviousRecommendation();
    } else {
      setProductList([]);
    }
  }, [selectedSubTab]);

  useEffect(() => {
    const fetchRenderedProducts = async () => {
      const blacklistedProductHandles = [
        "smokey-sneaker-crate",
        "powerpuff-hype-pack-of-3",
      ];

      console.log("productList", productList);

      const productHtmls = await Promise.all(
        productList
          ?.filter((e) => !blacklistedProductHandles.includes(e.handle))
          .map((item) => renderCloneNode(item)),
      );
      setUiProducts(productHtmls.map((html) => html.__html));
    };

    fetchRenderedProducts();
    getCollectionProductHandles();
  }, [productList]);

  useEffect(() => {
    if (window.location.pathname.includes("/checkout")) return;

    const firstMatchingTab = Object.keys(memoryData).find((item) =>
      collectionSubCategories.includes(item.toLowerCase()),
    );

    if (firstMatchingTab && firstMatchingTab !== selectedTab) {
      setSelectedTab(firstMatchingTab);
    } else if (!firstMatchingTab) {
      setSelectedTab(null); //If no tab available then set the selected tab to null
    }
  }, [collectionSubCategories, memoryData]);

  const isCheckoutPage = window.location.pathname.includes("/checkout");

  return (
    <>
      {Object.keys(memoryData).some((item) =>
        !isCheckoutPage
          ? collectionSubCategories.includes(item.toLowerCase())
          : true,
      ) && (
        <div
          className={cn("hp-py-6 hp-mx-auto container", {
            "hp-px-4": !window.location.pathname.includes("/checkout"),
          })}
        >
          {Object.keys(memoryData).length > 0 ? (
            <>
              <div className="hp-heading">
                {window.location.pathname.includes("/checkout")
                  ? "Nothing in your cart... yet!"
                  : "You have your eyes on"}
              </div>
              <div className="hp-mb-4 swimlane2">
                <ul
                  id="tabs"
                  className="hp-inline-flex hp-pt-2 hp-px-1 hp-w-full hp-border-b hp-h-[50px] hp-cursor-pointer hp-min-w-[calc(100vw-32px)]"
                >
                  {window.location.pathname.includes("product") && (
                    <PreviousRecommendations
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                      setSelectedSubTab={setSelectedSubTab}
                      type="collection"
                    />
                  )}

                  {Object.keys(memoryData).map((item, index) => {
                    if (
                      !collectionSubCategories.includes(item?.toLowerCase()) &&
                      !isCheckoutPage
                    ) {
                      return null; // Don't render the tab if it's not in collectionSubCategories
                    }

                    const subTabList = getFormattedFunctionalPreferences(
                      memoryData,
                      item,
                    );
                    if (subTabList.length === 0) return null;

                    const isActive = selectedTab === item;

                    return (
                      <li
                        key={index}
                        className={`hp-px-4 hp-text-gray-800 hp-whitespace-nowrap hp-py-2 hp-rounded-t hp-text-sm ${
                          isActive
                            ? "hp-bg-white hp-border-t hp-border-r hp-border-l -hp-mb-px"
                            : "hp-max-w-[200px]"
                        }`}
                        onClick={() => handleTabClick(item)}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          ) : null}

          {selectedTab !== previousTab && (
            <>
              <div className="hp-flex hp-gap-2 hp-overflow-x-auto hp-whitespace-nowrap hp-rounded-md hp-px-2 no-scrollbar md:hp-px-12 md:hp-mt-[-16px]">
                {subTabs.length > 0
                  ? subTabs?.map((item, index) => {
                      if (item.tabName.toLowerCase().includes("false")) return;

                      return (
                        <span
                          key={index}
                          className={cn(
                            "hp-inline-flex hp-cursor-pointer hp-items-center hp-gap-x-1.5 hp-rounded-full hp-bg-gray-100 hp-px-3 hp-py-1 hp-text-xs hp-font-medium hp-text-gray-600",
                            {
                              "hp-border hp-border-black hp-text-black":
                                selectedSubTab === item?.filterTagName,
                            },
                          )}
                          onClick={() => setSelectedSubTab(item?.filterTagName)}
                        >
                          {item.tabName.split("True")[1] || item?.tabName}
                        </span>
                      );
                    })
                  : null}
              </div>
            </>
          )}
          <div className="hp-mt-2 swimlane2 hp-items-baseline hp-min-h-[225px]">
            {isLoading
              ? Array.apply(null, Array(3)).map((_, index) => (
                  <CardSkeleton index={index} />
                ))
              : uiProducts
                ? uiProducts.map((html, i) => (
                    <div
                      key={i}
                      dangerouslySetInnerHTML={{ __html: html }}
                      className="hp-w-[200px]"
                    />
                  ))
                : null}
          </div>
        </div>
      )}
    </>
  );
};
